import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Users, Info } from 'lucide-react';
import { getUserBatches } from '../../firebase/collections';
import BatchListingModal from './BatchListingModal';

const BatchEnrollmentButton = ({ userId }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBatchListing, setShowBatchListing] = useState(false);

  const loadUserBatches = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getUserBatches(userId);
      if (result.success) {
        setBatches(result.data || []);
      }
    } catch (error) {
      // Error loading batches
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadUserBatches();
    } else {
      setLoading(false);
    }
  }, [userId, loadUserBatches]);

  if (loading) {
    return null;
  }

  if (batches.length === 0) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
              <BookOpen className="w-5 h-5" />
              <p className="text-sm">You aren't enrolled in any batch</p>
            </div>
            <motion.button
              onClick={() => setShowBatchListing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Info className="w-4 h-4" />
              <span>View Available Batches</span>
            </motion.button>
          </div>
        </motion.div>

        {showBatchListing && (
          <BatchListingModal
            userId={userId}
            onClose={() => setShowBatchListing(false)}
            onEnrollmentSuccess={() => {
              setShowBatchListing(false);
              loadUserBatches();
            }}
          />
        )}
      </>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 mb-6 text-white"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-white/20 rounded-lg p-3">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Your Batches</h3>
            <p className="text-sm text-blue-100">
              {batches.length} batch{batches.length > 1 ? 'es' : ''} available
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {batches.map((batch) => (
            <Link
              key={batch.id}
              to={`/batch/${batch.id}`}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors backdrop-blur-sm border border-white/30"
            >
              <BookOpen className="w-4 h-4 inline mr-2" />
              {batch.name}
            </Link>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default BatchEnrollmentButton;

